/**
 * Stato server del profilo personale (F-090).
 *
 * ⚠️ Le password non passano mai da TanStack Query come dato: sono argomenti di
 * mutazione e nient'altro. Nessuna `queryKey` le contiene, quindi non finiscono
 * nella cache né in un eventuale devtools.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type Session = {
  /** Identificativo della famiglia di token: è la sessione. */
  id: string;
  /** La sessione da cui si sta guardando la pagina. */
  current: boolean;
  startedAt: string;
  /**
   * Ultima rotazione del token. **Non** «ultima attività»: gli access token
   * durano 15 minuti, quindi una sessione in uso ruota ogni quarto d'ora e una
   * lasciata aperta in una scheda non ruota affatto.
   */
  lastRotatedAt: string;
  expiresAt: string;
  device: string;
  userAgent: string | null;
  ipAddress: string | null;
};

export type LoginEvent = {
  at: string;
  device: string;
  userAgent: string | null;
  ipAddress: string | null;
  outcome: 'active' | 'ended' | 'expired';
};

export type LoginLog = {
  items: LoginEvent[];
  /** Giorni di storia conservati: la pagina lo dice invece di sembrare completa. */
  retentionDays: number;
};

export const profileKeys = {
  sessions: ['profile', 'sessions'] as const,
  logins: ['profile', 'logins'] as const,
};

export function useSessions() {
  return useQuery({
    queryKey: profileKeys.sessions,
    queryFn: ({ signal }) => api.get<{ items: Session[] }>('/auth/sessions', signal),
  });
}

export function useLoginLog() {
  return useQuery({
    queryKey: profileKeys.logins,
    queryFn: ({ signal }) => api.get<LoginLog>('/auth/logins', signal),
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (familyId: string) => api.delete<{ revoked: number }>(`/auth/sessions/${familyId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.sessions });
      // Chiudere una sessione cambia anche l'esito del suo accesso nel
      // registro: da «attiva» a «terminata».
      void queryClient.invalidateQueries({ queryKey: profileKeys.logins });
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post<{ closedSessions: number }>('/auth/change-password', input),
    onSuccess: () => {
      // Il cambio password chiude le altre sessioni: l'elenco è cambiato.
      void queryClient.invalidateQueries({ queryKey: profileKeys.sessions });
      void queryClient.invalidateQueries({ queryKey: profileKeys.logins });
    },
  });
}
